import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Field, reduxForm } from "redux-form";
import { required } from "redux-form-validators";
import { renderField } from "../../Components/Forms/renders";
import mask from "../../Components/Forms/mask";
import "./accept.css";

const InviteAcceptForm = ({ handleSubmit, loading, locationName, email, successMessage }) => {
  return (
    <div className="invite-accept-wrapper">
      <div className="invite-accept-container">
        <div className="invite-card">
          <div className="invite-header">
            <div className="invite-logo">
              <i className="fa fa-envelope-open"></i>
            </div>
            <h1>Welkom!</h1>
            <p className="invite-subtitle">Voltooi je registratie</p>
          </div>

          <div className="invite-content">
            {locationName && (
              <div className="alert alert-info">
                <i className="fa fa-info-circle"></i>
                <span>Je bent uitgenodigd om <strong>{locationName}</strong> te beheren.</span>
              </div>
            )}

            {successMessage && (
              <div className="alert alert-success">
                <i className="fa fa-check-circle"></i>
                <span>{successMessage}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="invite-form">
              <div className="form-section">
                <h3 className="form-section-title">Account Gegevens</h3>

                <div className="form-group">
                  <label className="form-label">E-mailadres</label>
                  <input
                    type="email"
                    className="form-control form-control-static"
                    value={email}
                    disabled
                    readOnly
                  />
                  <small className="form-text text-muted">Dit is het e-mailadres waarmee je uitgenodigd bent.</small>
                </div>

                <div className="form-row">
                  <div className="form-col">
                    <Field
                      name="voornaam"
                      type="input"
                      validate={[required()]}
                      component={renderField}
                      label="Voornaam"
                      placeholder="Bijv. Jan"
                      className="form-control"
                    />
                  </div>
                  <div className="form-col">
                    <Field
                      name="achternaam"
                      type="input"
                      validate={[required()]}
                      component={renderField}
                      label="Achternaam"
                      placeholder="Bijv. de Vries"
                      className="form-control"
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3 className="form-section-title">Beveiliging</h3>

                <Field
                  name="password"
                  type="password"
                  validate={[required()]}
                  component={renderField}
                  label="Wachtwoord"
                  placeholder="Kies een sterk wachtwoord"
                  className="form-control"
                />
                <div className="password-requirements">
                  <small className="form-text text-muted">
                    <strong>Vereisten:</strong>
                    <ul>
                      <li>Minimaal 8 karakters</li>
                      <li>Minstens 1 hoofdletter (A-Z)</li>
                      <li>Minstens 1 kleine letter (a-z)</li>
                      <li>Minstens 1 getal (0-9)</li>
                    </ul>
                    <strong>Voorbeeld:</strong> Pass123
                  </small>
                </div>
              </div>

              <div className="form-section">
                <h3 className="form-section-title">Contact Informatie</h3>

                <Field
                  name="telefoonnummer"
                  type="input"
                  mask="39999999999"
                  validate={[required()]}
                  component={mask}
                  label="Telefoonnummer"
                  placeholder="+31 6 12345678"
                  className="form-control"
                />
                <small className="form-text text-muted">
                  We gebruiken dit voor belangrijke communicatie.
                </small>
              </div>

              <button
                type="submit"
                className="btn btn-primary btn-submit"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <i className="fa fa-spinner fa-spin"></i>
                    <span>Account wordt aangemaakt...</span>
                  </>
                ) : (
                  <>
                    <i className="fa fa-check"></i>
                    <span>Account Aanmaken &amp; Aanmelden</span>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

const InviteAcceptFormWrapped = reduxForm({
  form: "invite_accept"
})(InviteAcceptForm);

const InviteAcceptPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [locationName, setLocationName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validating, setValidating] = useState(true);
  const [successMessage, setSuccessMessage] = useState("");

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      try {
        const response = await fetch(`/api/invite/validate/${token}`);
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.msg || "Invalid or expired invitation link");
        }

        setLocationName(data.locationName);
        setEmail(data.email);
      } catch (err) {
        setError(err.message);
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (values) => {
    setLoading(true);
    setError(null);
    setSuccessMessage("");

    try {
      const response = await fetch(`/api/invite/accept/${token}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(values)
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle validation errors
        if (data.errors && Array.isArray(data.errors)) {
          throw new Error(data.errors[0].message || data.msg);
        }
        throw new Error(data.msg || "Er is een fout opgetreden");
      }

      // Show success message
      setSuccessMessage("Account succesvol aangemaakt! Je wordt doorgestuurd naar de login pagina...");

      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="sneat-auth-inner">
        <div className="invite-card loading-card">
          <div className="loading-spinner">
            <i className="fa fa-spinner fa-spin"></i>
          </div>
          <p className="loading-text">Bezig met valideren van je uitnodiging...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sneat-auth-inner">
        <div className="invite-card error-card">
          <div className="error-icon">
            <i className="fa fa-exclamation-circle"></i>
          </div>
          <h2>Uitnodiging Ongeldig</h2>
          <p className="error-message">{error}</p>
          <div className="error-help">
            <p><small>Mogelijke redenen:</small></p>
            <ul>
              <li>De link is verlopen (geldig 7 dagen)</li>
              <li>De link is al gebruikt</li>
              <li>De link is ongeldig</li>
            </ul>
            <p><small>Neem contact op met je beheerder voor een nieuwe uitnodiging.</small></p>
          </div>
          <button
            onClick={() => navigate("/")}
            className="btn btn-secondary"
          >
            <i className="fa fa-home"></i>
            Terug naar start
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="sneat-auth-inner">
      <InviteAcceptFormWrapped
        onSubmit={handleSubmit}
        loading={loading}
        locationName={locationName}
        email={email}
        successMessage={successMessage}
      />
    </div>
  );
};

export default InviteAcceptPage;
